import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { JwtAuthService } from '../../../shared/services/auth/jwt-auth.service';

export interface WhatsAppConfig {
  instanceName: string;
  apiUrl: string;
  apiKey: string;
}

export interface InstanceStatus {
  instance: {
    instanceName: string;
    status: 'open' | 'close' | 'connecting';
  };
  qrcode?: {
    base64: string;
    code: string;
  };
}

export interface ConnectionResponse {
  instance: {
    instanceName: string;
    status: string;
  };
  hash?: {
    apikey: string;
  };
  qrcode?: {
    base64: string;
    code: string;
  };
  base64?: string; // QR Code base64 direto na resposta
}

@Injectable({
  providedIn: 'root'
})
export class WhatsAppConfigService {
  
  constructor(private http: HttpClient, private jwtAuthService: JwtAuthService) {}

  /**
   * Salva ou atualiza a configuração da Evolution API
   */
  saveConfiguration(config: WhatsAppConfig): Observable<any> {
    console.log('Salvando configuração da Evolution API:', config);
    console.log('API URL:', `${this.jwtAuthService.apiUrl}/evolution/instance/create`);

    return this.http.post(`${this.jwtAuthService.apiUrl}/evolution/instance/create`, config)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Obtém a configuração atual da Evolution API
   */
  getConfiguration(): Observable<WhatsAppConfig> {
    return this.http.get<WhatsAppConfig>(`${this.jwtAuthService.apiUrl}/evolution/config`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Testa a conexão com a API Evolution
   */
  testConnection(config: WhatsAppConfig): Observable<boolean> {
    const headers = this.createHeaders(config.apiKey);
    return this.http.get(`${this.jwtAuthService.apiUrl}/evolution/health`, { headers })
      .pipe(
        map(() => true),
        catchError(() => throwError(() => new Error('Falha ao conectar com a API')))
      );
  }

  /**
   * Cria uma nova instância no Evolution API
   */
  createInstance(instanceName: string): Observable<ConnectionResponse> {
    console.log('Criando nova instância:', instanceName);
    return this.http.post<ConnectionResponse>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/create`,
      { instanceName }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtém o QR Code para conectar uma instância
   * Este endpoint retorna o QR code que precisa ser escaneado para conectar a conta do WhatsApp
   */
  connectInstance(instanceName: string): Observable<ConnectionResponse> {
    return this.http.get<ConnectionResponse>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/connect/${instanceName}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtém o status de uma instância
   */
  getInstanceStatus(instanceName: string): Observable<InstanceStatus> {
    return this.http.get<InstanceStatus>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/connectionState/${instanceName}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtém o QR Code de uma instância
   */
  getQrCode(instanceName: string): Observable<string> {
    return this.http.get<any>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/connect/${instanceName}`
    ).pipe(
      map(response => {
        if (response.qrcode && response.qrcode.base64) {
          return response.qrcode.base64;
        }
        throw new Error('QR Code não disponível');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Desconecta (faz logout) de uma instância
   */
  disconnectInstance(instanceName: string): Observable<any> {
    // O endpoint no backend expõe POST /instance/logout/{instanceName}
    return this.http.post(
      `${this.jwtAuthService.apiUrl}/evolution/instance/logout/${instanceName}`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Reinicia uma instância
   */
  restartInstance(instanceName: string): Observable<any> {
    return this.http.put(
      `${this.jwtAuthService.apiUrl}/evolution/instance/restart/${instanceName}`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Deleta uma instância
   */
  deleteInstance(instanceName: string): Observable<any> {
    return this.http.delete(
      `${this.jwtAuthService.apiUrl}/evolution/instance/delete/${instanceName}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Verifica se a instância está conectada
   */
  isInstanceConnected(instanceName: string): Observable<boolean> {
    return this.getInstanceStatus(instanceName).pipe(
      map(status => status.instance.status === 'open'),
      catchError(() => throwError(() => new Error('Erro ao verificar status')))
    );
  }

  /**
   * Lista todas as instâncias configuradas
   */
  listInstances(): Observable<any[]> {
    return this.http.get<any[]>(`${this.jwtAuthService.apiUrl}/evolution/instance/fetchInstances`)
      .pipe(
        catchError(this.handleError)
      );
  }

  
  
  /**
   * Busca uma instância específica pelo nome
   */
  fetchInstance(instanceName: string): Observable<any> {
    
    return this.http.get<any>(`${this.jwtAuthService.apiUrl}/evolution/instance/fetchInstances/${instanceName}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Cria headers HTTP com a API Key
   */
  private createHeaders(apiKey: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'apikey': apiKey
    });
  }

  /**
   * Tratamento de erros HTTP
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Erro desconhecido';
    
    if (error.error instanceof ErrorEvent) {
      // Erro do lado do cliente
      errorMessage = `Erro: ${error.error.message}`;
    } else {
      // Erro do lado do servidor
      if (error.status === 0) {
        errorMessage = 'Não foi possível conectar ao servidor';
      } else if (error.status === 401) {
        errorMessage = 'Não autorizado - verifique a API Key';
      } else if (error.status === 404) {
        errorMessage = 'Recurso não encontrado';
      } else if (error.status === 500) {
        errorMessage = 'Erro interno do servidor';
      } else if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else {
        errorMessage = `Erro ${error.status}: ${error.message}`;
      }
    }

    console.error('Erro na requisição:', error);
    return throwError(() => new Error(errorMessage));
  }
}
